import {
  type Control,
  type FieldValues,
  type Path,
  useWatch,
} from "react-hook-form";
import { formatCurrency } from "../lib/numbers";

/** `$4,868`, or `−$5,132` for negative cash. */
const formatCash = (value: number) =>
  `${value < 0 ? "−" : ""}${formatCurrency(Math.abs(value))}`;

/** The Raise or Spend Cash amount as typed: a positive number, or `null`. */
const enteredAmount = (value: unknown): number | null => {
  const amount =
    typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

/**
 * The warning a Raise Cash or Spend Cash amount needs, given the cash the
 * proposal will run with, or `null` when it needs none. Exported for the
 * tests.
 *
 * - Raise Cash on negative reported cash: the proposal raises exactly the
 *   amount entered, so it leaves the shortfall; say what it ends with and
 *   what to raise instead. This message names no adjustment: an intraday
 *   adjustment may take the cash below $0 (a Tolerance proposal sells to
 *   cover it), but Raise Cash never reads it as a shortfall to cover.
 * - Spend Cash on no cash: the proposal spends nothing, so it comes back
 *   empty; say so, and whether the adjustment is what left no cash.
 */
export function raiseSpendCashWarning({
  type,
  effectiveCash,
  adjusted,
  amount,
}: {
  type: "raise_cash" | "spend_cash";
  /** Reported cash plus the intraday adjustment, if any. */
  effectiveCash: number;
  /** Whether the advisor entered an intraday cash adjustment. */
  adjusted: boolean;
  /** The amount entered, or `null` when none is entered yet. */
  amount: number | null;
}): string | null {
  if (type === "raise_cash") {
    if (effectiveCash >= 0) return null;
    const cash = `This account has ${formatCash(effectiveCash)} cash`;
    if (amount === null) {
      return `${cash}; include the shortfall in the amount to raise.`;
    }
    return (
      `${cash}. Raising ${formatCash(amount)} will leave` +
      ` ${formatCash(effectiveCash + amount)}. Raise` +
      ` ${formatCash(amount - effectiveCash)} to end with` +
      ` ${formatCash(amount)}.`
    );
  }
  if (effectiveCash > 0) return null;
  const afterThe = adjusted ? " after the adjustment" : "";
  return `No cash available to spend${afterThe} (${formatCash(effectiveCash)}).`;
}

/**
 * The warning under one account's Raise or Spend Cash amount on the Run
 * Trade Proposal page. It does not block the submit; it watches the amount
 * so it follows what the advisor types.
 */
export default function RaiseSpendCashWarning<
  TFieldValues extends FieldValues,
>({
  control,
  name,
  type,
  effectiveCash,
  adjusted,
}: {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  type: "raise_cash" | "spend_cash";
  effectiveCash: number;
  adjusted: boolean;
}) {
  const value = useWatch({ control, name });
  const warning = raiseSpendCashWarning({
    type,
    effectiveCash,
    adjusted,
    amount: enteredAmount(value),
  });
  return warning === null ? null : (
    <small
      className="text-warning d-block mt-1"
      role="status"
      data-testid="raise-spend-cash-warning"
    >
      {warning}
    </small>
  );
}
